import React from "react";
import { HiX } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import {
  WHITELABEL_LOGO_MODE,
  WHITELABEL_SIDENAV_LAYOUT,
} from "constants/moduleConstants/whitelabel.constant";
import { MOLE_LOGO_FULL } from "components/template/Logo";

const DEFAULT_PRIMARY = "#ccdb7c";

const resolvePrimaryColor = (value) => {
  const trimmed = (value || "").trim();
  return trimmed || DEFAULT_PRIMARY;
};

const CompanyLogo = ({ src, alt, className }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        draggable={false}
      />
    );
  }

  return (
    <span
      className={`inline-flex min-h-[28px] items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 text-xs font-medium text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500 ${className}`}
    >
      Logo
    </span>
  );
};

const MoleLogo = ({ className }) => (
  <img
    src={MOLE_LOGO_FULL}
    alt="Mol-e"
    className={className}
    draggable={false}
  />
);

const AuthLogoArea = ({ logoMode, companyLogoSrc, t }) => {
  if (logoMode === WHITELABEL_LOGO_MODE.NONE) {
    return <MoleLogo className="h-10 w-auto max-w-[180px] object-contain" />;
  }

  if (logoMode === WHITELABEL_LOGO_MODE.STANDALONE) {
    return (
      <CompanyLogo
        src={companyLogoSrc}
        alt={t("companies.form.whitelabelFields.preview.companyLogoPlaceholder")}
        className="h-10 max-w-[220px] object-contain"
      />
    );
  }

  return (
    <div className="flex w-full min-w-0 items-center justify-center gap-4 px-2">
      <CompanyLogo
        src={companyLogoSrc}
        alt={t("companies.form.whitelabelFields.preview.companyLogoPlaceholder")}
        className="h-9 max-w-[120px] flex-1 object-contain"
      />
      <HiX className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
      <MoleLogo className="-mt-1 h-9 w-auto max-w-[120px] flex-1 object-contain" />
    </div>
  );
};

const SidenavLogoArea = ({ logoMode, sidenavLogoLayout, companyLogoSrc, t }) => {
  if (
    logoMode === WHITELABEL_LOGO_MODE.NONE ||
    (logoMode === WHITELABEL_LOGO_MODE.WITH_MOLE &&
      sidenavLogoLayout === WHITELABEL_SIDENAV_LAYOUT.NONE)
  ) {
    return (
      <div className="flex min-h-[72px] items-center justify-center border-b border-gray-200 px-4 py-4 dark:border-gray-700">
        <MoleLogo className="h-8 w-auto max-w-[160px] object-contain opacity-90" />
      </div>
    );
  }

  if (logoMode === WHITELABEL_LOGO_MODE.STANDALONE) {
    return (
      <div className="flex min-h-[72px] items-center justify-center border-b border-gray-200 px-4 py-4 dark:border-gray-700">
        <CompanyLogo
          src={companyLogoSrc}
          alt={t("companies.form.whitelabelFields.preview.companyLogoPlaceholder")}
          className="h-10 max-w-[200px] object-contain"
        />
      </div>
    );
  }

  if (sidenavLogoLayout === WHITELABEL_SIDENAV_LAYOUT.HORIZONTAL) {
    return (
      <div className="flex min-h-[72px] items-center justify-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <CompanyLogo
          src={companyLogoSrc}
          alt={t("companies.form.whitelabelFields.preview.companyLogoPlaceholder")}
          className="h-8 max-w-[96px] flex-1 object-contain"
        />
        <HiX className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
        <MoleLogo className="-mt-1 h-8 w-auto max-w-[96px] flex-1 object-contain" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[72px] items-center justify-center border-b border-gray-200 px-4 py-4 dark:border-gray-700">
      <CompanyLogo
        src={companyLogoSrc}
        alt={t("companies.form.whitelabelFields.preview.companyLogoPlaceholder")}
        className="h-10 max-w-[200px] object-contain"
      />
    </div>
  );
};

const AuthPreview = ({ logoMode, companyLogoSrc, primaryColor, t }) => {
  const accent = resolvePrimaryColor(primaryColor);

  return (
    <div className="rounded-xl bg-gray-100 p-6 dark:bg-gray-900/60">
      <div className="mx-auto w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-600 dark:bg-gray-800">
        <div className="mb-5 flex min-h-[52px] items-center justify-center">
          <AuthLogoArea
            logoMode={logoMode}
            companyLogoSrc={companyLogoSrc}
            t={t}
          />
        </div>
        <div className="mb-3 h-9 rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-900/40" />
        <div className="mb-5 h-9 rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-900/40" />
        <div
          className="rounded-lg py-2.5 text-center text-sm font-semibold text-gray-800"
          style={{ backgroundColor: accent }}
        >
          {t("companies.form.whitelabelFields.preview.signInButton")}
        </div>
        <p className="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">
          {t("companies.form.whitelabelFields.preview.primaryColorHint")}
        </p>
      </div>
    </div>
  );
};

const SidenavPreview = ({
  logoMode,
  sidenavLogoLayout,
  companyLogoSrc,
  t,
}) => {
  const showMoleFooter =
    logoMode === WHITELABEL_LOGO_MODE.WITH_MOLE &&
    sidenavLogoLayout === WHITELABEL_SIDENAV_LAYOUT.FOOTER;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800">
      <div className="flex min-h-[220px]">
        <div className="flex w-[240px] shrink-0 flex-col border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <SidenavLogoArea
            logoMode={logoMode}
            sidenavLogoLayout={sidenavLogoLayout}
            companyLogoSrc={companyLogoSrc}
            t={t}
          />
          <div className="flex-1 space-y-2.5 px-4 py-4">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="flex items-center gap-2" aria-hidden>
                <span className="h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                <span className="h-2 flex-1 rounded bg-gray-200 dark:bg-gray-700" />
              </div>
            ))}
          </div>
          {showMoleFooter ? (
            <div className="flex min-h-[56px] items-center justify-center border-t border-gray-200 px-4 py-3 dark:border-gray-700">
              <MoleLogo className="h-7 w-auto max-w-[120px] object-contain opacity-80" />
            </div>
          ) : null}
        </div>
        <div className="flex-1 bg-gray-50 p-5 dark:bg-gray-900/30">
          <div className="mb-3 h-2.5 w-24 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="space-y-2">
            <div className="h-10 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800" />
            <div className="h-10 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800" />
            <div className="h-24 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800" />
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewPanel = ({ title, children }) => (
  <div>
    <p className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
      {title}
    </p>
    {children}
  </div>
);

const WhitelabelConfigPreview = ({
  logoMode,
  sidenavLogoLayout,
  companyLogoSrc,
  primaryColor,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <PreviewPanel title={t("companies.form.whitelabelFields.preview.auth")}>
        <AuthPreview
          logoMode={logoMode}
          companyLogoSrc={companyLogoSrc}
          primaryColor={primaryColor}
          t={t}
        />
      </PreviewPanel>
      <PreviewPanel title={t("companies.form.whitelabelFields.preview.sidenav")}>
        <SidenavPreview
          logoMode={logoMode}
          sidenavLogoLayout={sidenavLogoLayout}
          companyLogoSrc={companyLogoSrc}
          t={t}
        />
      </PreviewPanel>
    </div>
  );
};

export default WhitelabelConfigPreview;
